import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Switch,
  Paper,
  Avatar,
  IconButton,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import apiService from "../../../service/Api";

interface FavoriteItem {
  id: number;
  name: string;
  description: string;
  icon_name?: string;
  icon_bg?: string;
}

const FavoritesPanel = ({
  reduxTheme,
  deceasedId,
}: {
  reduxTheme: any;
  deceasedId?: number;
}) => {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [editingItem, setEditingItem] = useState<FavoriteItem | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editIcon, setEditIcon] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!deceasedId) return; // no id yet, skip fetch

    const fetchFavorites = async () => {
      try {
        const response = await apiService.get<{ favorites: FavoriteItem[] }>(
          `api/client-portal/favourites/?deceased_id=${deceasedId}`
        );

        const favoritesData =
          typeof response.data === "string"
            ? JSON.parse(response.data)
            : response.data;

        // Handle both object-with-favorites and array response types safely
        setFavorites(
          Array.isArray(favoritesData)
            ? favoritesData
            : favoritesData.favorites ?? []
        );
      } catch (err) {
        console.error("Failed to fetch favorites", err);
        setFavorites([]);
      }
    };

    fetchFavorites();
  }, [deceasedId]);

  const iconOptions = [
    { value: "quote", label: "Quote", icon: <FormatQuoteIcon /> },
    { value: "love", label: "Love", icon: <FavoriteIcon /> },
    { value: "coffee", label: "Coffee", icon: <LocalCafeIcon /> },
    { value: "book", label: "Book", icon: <MenuBookIcon /> },
  ];

  const getIconComponent = (iconValue?: string) => {
    const option = iconOptions.find((opt) => opt.value === iconValue);
    return option ? option.icon : <FormatQuoteIcon />;
  };

  const handleSaveEdit = async () => {
    if (!editingItem || !deceasedId) return;

    try {
      const payload = {
        name: editName,
        description: editDescription,
        icon_name: editIcon,
        icon_bg: editingItem.icon_bg,
        deceased: deceasedId,
      };

      const response = await apiService.patch<FavoriteItem>(
        `api/client-portal/favourites/${editingItem.id}/?deceased_id=${deceasedId}`,
        payload
      );

      const updatedFav: FavoriteItem = response.data || {
        ...editingItem,
        ...payload,
      };

      setFavorites((prev) =>
        prev.map((f) => (f.id === editingItem.id ? updatedFav : f))
      );
      setEditingItem(null);
    } catch (err) {
      console.error("Failed to update favourite", err);
    }
  };

  return (
    <Card
      sx={{
        height: 482,
        backgroundColor: reduxTheme.primary_colors?.[800],
        borderRadius: 3,
        pt: 8,
        width: 660,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2, height: "100%", overflow: "hidden" }}>
        <Box
          display="flex"
          flexDirection="column"
          gap={1.5}
          sx={{ overflow: "hidden" }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", mb: 2 }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Choose favorites to display
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Enable/Disable
              </Typography>
              <Switch size="small" defaultChecked />
            </Box>
          </Box>

          <Box
            display="grid"
            gridTemplateColumns="repeat(2, 1fr)"
            gap={1.5}
            height="100%"
            pr={1}
          >
            {favorites.length > 0 ? (
              favorites.map((fav) => (
                <Paper
                  key={fav.id}
                  sx={{
                    p: 2,
                    position: "relative",
                    borderRadius: 4,
                    backgroundColor: reduxTheme.primary_colors?.[900],
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label="edit"
                    onClick={() => {
                      setEditingItem(fav);
                      setEditName(fav.name);
                      setEditDescription(fav.description);
                      setEditIcon(fav.icon_name);
                    }}
                    sx={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      width: 18,
                      height: 18,
                      bgcolor: "background.paper",
                      boxShadow: 1,
                      borderRadius: "50%",
                      "&:hover": { bgcolor: "grey.100" },
                    }}
                  >
                    <BorderColorOutlinedIcon sx={{ fontSize: 10 }} />
                  </IconButton>

                  <Avatar
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      bgcolor: fav.icon_bg ?? reduxTheme.primary_colors?.[400],
                      width: 24,
                      height: 24,
                      color: "#6D4C41",
                    }}
                  >
                    {getIconComponent(fav.icon_name)}
                  </Avatar>

                  <Box sx={{ pr: 6 }}>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color={
                        reduxTheme.primary_colors?.[400] ?? "text.secondary"
                      }
                      gutterBottom
                    >
                      {fav.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {fav.description}
                    </Typography>

                    <Switch
                      size="small"
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 4,
                        transform: "scale(0.9)",
                        transformOrigin: "bottom right",
                      }}
                    />
                  </Box>
                </Paper>
              ))
            ) : (
              <Typography
                sx={{ gridColumn: "1 / -1", color: "text.secondary" }}
              >
                No favorites available.
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>

      {editingItem && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Paper sx={{ p: 4, width: 400, borderRadius: 2 }}>
            <Typography variant="h6" mb={2}>
              Edit Favorite
            </Typography>
            <TextField
              label="Name"
              fullWidth
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              select
              label="Icon"
              fullWidth
              value={editIcon}
              onChange={(e) => setEditIcon(e.target.value)}
              sx={{ mb: 2 }}
              SelectProps={{
                MenuProps: { disablePortal: true, style: { zIndex: 10000 } },
              }}
            >
              {iconOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" onClick={() => setEditingItem(null)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveEdit}>
                Save
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Card>
  );
};

export default FavoritesPanel;
